/* eslint-disable react/jsx-props-no-spreading */
import { forwardRef, useMemo } from 'react';

// libs
import Select, { components } from 'react-select';
import { isNil } from 'lodash';
// utils
import { cn } from '@repo/zolintra-utils';
// components
import { Icon } from '../Icon/Icon';

// styles
import getCustomStyles from './DropdownListStyles';
import styles from './DropdownList.module.css';

function DropdownIndicator(props) {
  return components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      <Icon icon="arrowDown" style={{ height: 16, width: 16 }} />
    </components.DropdownIndicator>
  );
}

function ClearIndicator(props) {
  return components.ClearIndicator && (
    <components.ClearIndicator {...props}>
      <Icon icon="cross" style={{ height: 16, width: 16 }} />
    </components.ClearIndicator>
  );
}

function Option(props) {
  const { label, data } = props;
  return components.Option && (
    <components.Option {...props}>
      {data?.icon || null}
      {label}
    </components.Option>
  );
}

/**
 * `DropdownList` component for selecting options in a list.
 *
 * @typedef {Object} Props The properties for the `TextField` component.
 * @property {string} [name] The name of the text field, used for identification.
 * @property {string} [label] The label associated with the text field.
 * @property {string} [placeholder] The label associated with the text field.
 * @property {Array<Object>} [options=[]] The available options.
 * @property {Object|Array<Object>} [props.value] The selected value(s).
 * @property {function} [props.onChange] The onChange function.
 * @property {string} noOptionsMessage The message to display if no option is available.
 * @property {boolean} [isMulti=false] If `true`, multiple options can be selected.
 * @property {string} [error] The error message to display below the field if an error exists.
 * @property {boolean} [isClearable=true] If `true`, the field can be cleared with the clear button.
 * @property {number} [maxWidth] The max width of the field.
 * @property {string} [className] Additional CSS classes to apply to the component.
 * @property {boolean} [isInsideDialog=false] Indicates that the component is inside a dialog.
 * @property {boolean} [disabled=false] If `true`, the field will be disabled.
 * @property {import('react').ComponentPropsWithoutRef<"select">} [props.props]
 */

/**
 * @type React.ForwardRefExoticComponent<Props>
 */
export const DropdownList = forwardRef(({
  name,
  label,
  placeholder,
  options,
  noOptionsMessage,
  isMulti,
  error,
  isClearable,
  maxWidth,
  className,
  isInsideDialog,
  disabled,
  ...props
}, ref) => {
  const customStyles = useMemo(() => (
    getCustomStyles({ hasError: !isNil(error) })
  ), [error]);

  return (
    <div className={cn([styles.field, className])} style={maxWidth ? { maxWidth } : {}}>
      {label ? (
        <p className={styles.label}>{label}</p>
      ) : null}

      {/* @ts-ignore */}
      <Select
        ref={ref}
        name={name}
        options={options}
        menuPosition="fixed"
        // use isInsideDialog to prevent menu target outside dialog
        // and keep menu visible in front of dialog
        menuPortalTarget={isInsideDialog ? document.querySelector('root') : document.body}
        noOptionsMessage={() => noOptionsMessage}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        className={styles.selectortest}
        styles={customStyles}
        isClearable={isClearable}
        isDisabled={disabled}
        components={{ DropdownIndicator, ClearIndicator, Option }}
        placeholder={placeholder}
        {...props}
      />

      {error ? <span id={name ? `error-${name}` : ''} className={styles.error}>{error}</span> : null}
    </div>
  );
});

export default DropdownList;
